/**
 * Deal Management API client
 */
import { getToken } from './api';

const BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');

async function dmReq(method, path, body = null) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const opts = { method, headers };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${BASE}/deal-management${path}`, opts);
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.error || `HTTP ${res.status}`);
    err.incomplete = json.incomplete;
    err.requiresOverride = json.requiresOverride;
    err.data = json;
    throw err;
  }
  return json;
}

const get  = (path) => dmReq('GET', path);
const post = (path, body) => dmReq('POST', path, body);
const put  = (path, body) => dmReq('PUT', path, body);

export const dealMgmt = {
  // Core
  dashboard:       () => get('/dashboard'),
  pipeline:        (params = {}) => get('/pipeline?' + new URLSearchParams(params)),
  team:            () => get('/team'),
  getDeal:         (id) => get(`/deals/${id}`),
  createDeal:      (data) => post('/deals', data),
  updateDeal:      (id, data) => put(`/deals/${id}`, data),
  assignDeal:      (id, data) => put(`/deals/${id}/assign`, data),
  updateStage:     (id, data) => put(`/deals/${id}/stage`, data),
  confirmRejectionRules: (id, data) => put(`/deals/${id}/stage1/rejection-rules`, data),
  recommendation:  (id, data) => put(`/deals/${id}/stage1/recommendation`, data),
  icVote:          (id, data) => put(`/deals/${id}/ic/vote`, data),
  updateFees:      (id, data) => put(`/deals/${id}/fees`, data),
  listFeeQuotes:   (id) => get(`/deals/${id}/fee-quotes`),
  regenerateFeeQuotes: (id, data) => post(`/deals/${id}/fee-quotes/regenerate`, data || {}),
  selectFeeQuote:  (id, quoteId) => put(`/deals/${id}/fee-quotes/${quoteId}/select`),
  sendFeeQuote:    (id, quoteId, data) => put(`/deals/${id}/fee-quotes/${quoteId}/send`, data || {}),
  feeQuotePdfUrl:  (id, quoteId) => {
    const token = getToken();
    const q = token ? `?token=${encodeURIComponent(token)}` : '';
    // Prefer Authorization header via fetch download helper
    return `${BASE}/deal-management/deals/${id}/fee-quotes/${quoteId}/pdf${q}`;
  },
  downloadFeeQuotePdf: async (id, quoteId) => {
    const headers = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${BASE}/deal-management/deals/${id}/fee-quotes/${quoteId}/pdf`, { headers });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return true;
  },
  updateWorkflow:  (id, data) => put(`/deals/${id}/workflow`, data),
  getStage1Calls:  (id) => get(`/deals/${id}/stage1/calls`),
  saveStage1Calls: (id, calls) => put(`/deals/${id}/stage1/calls`, { calls }),
  postUpdate:      (id, data) => post(`/deals/${id}/updates`, data),
  sendEmail:       (id, data) => post(`/deals/${id}/emails`, data),
  updateDocument:  (id, docId, data) => put(`/deals/${id}/documents/${docId}`, data),
  uploadDocumentFile: async (dealId, docId, file, extra = {}) => {
    const headers = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const form = new FormData();
    form.append('file', file);
    if (extra.comments) form.append('comments', extra.comments);
    const res = await fetch(`${BASE}/deal-management/deals/${dealId}/documents/${docId}/upload`, {
      method: 'POST',
      headers,
      body: form,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
    return json;
  },
  removeDocumentFile: (dealId, docId) => dmReq('DELETE', `/deals/${dealId}/documents/${docId}/file`),
  downloadDocumentFile: async (dealId, docId) => {
    const headers = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${BASE}/deal-management/deals/${dealId}/documents/${docId}/file`, {
      headers,
    });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || `HTTP ${res.status}`);
    }
    return res.blob();
  },
  authorisePayment:(id, data) => put(`/deals/${id}/finance/authorise`, data),
  calculate:       (data) => post('/calculate', data),
  notifications:   () => get('/notifications'),
  markRead:        (id) => put(`/notifications/${id}/read`),
  endClients:      () => get('/registers/end-clients'),
  createEndClient: (data) => post('/registers/end-clients', data),
  updateEndClient: (id, data) => put(`/registers/end-clients/${id}`, data),
  directors:       () => get('/registers/directors'),
  createDirector:  (data) => post('/registers/directors', data),
  lookupDirector:  (idNumber) => get(`/registers/directors/lookup?${new URLSearchParams({ id_number: idNumber })}`),
  importDirectorsExcel: async (file) => {
    const headers = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}/deal-management/registers/directors/import`, {
      method: 'POST',
      headers,
      body: form,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
    return json;
  },
  emailTemplates:  () => get('/templates/email'),
  createEmailTemplate: (data) => post('/templates/email', data),
  updateEmailTemplate: (id, data) => put(`/templates/email/${id}`, data),
  deleteEmailTemplate: (id) => dmReq('DELETE', `/templates/email/${id}`),
  contractTemplates: () => get('/templates/contract'),
  createContractTemplate: (data) => post('/templates/contract', data),
  updateContractTemplate: (id, data) => put(`/templates/contract/${id}`, data),
  deleteContractTemplate: (id) => dmReq('DELETE', `/templates/contract/${id}`),
  /** Aliases used by ops UI */
  registers:       () => get('/registers/end-clients'),
  templates:       () => get('/templates/email'),

  // Suppliers
  listSuppliers:   (id) => get(`/deals/${id}/suppliers`),
  createSupplier:  (id, data) => post(`/deals/${id}/suppliers`, data),
  updateSupplier:  (id, supplierId, data) => put(`/deals/${id}/suppliers/${supplierId}`, data),
  bankConfirmation:(id, supplierId, data) => put(`/deals/${id}/suppliers/${supplierId}/bank-confirmation`, data),

  // Supplier payments
  listSupplierPayments: (id) => get(`/deals/${id}/supplier-payments`),
  getSupplierPayment:   (id, supplierId) => get(`/deals/${id}/supplier-payments/${supplierId}`),
  updateSupplierPayment:(id, supplierId, data) => put(`/deals/${id}/supplier-payments/${supplierId}`, data),
  submitSupplierPayment:(id, supplierId, data) => put(`/deals/${id}/supplier-payments/${supplierId}/submit`, data),
  authoriseSupplierPayment: (id, supplierId, data) => put(`/deals/${id}/supplier-payments/${supplierId}/authorise`, data),
  uploadFnbScreenshot: (id, supplierId, data) => post(`/deals/${id}/supplier-payments/${supplierId}/fnb-screenshot`, data),

  // Team Lead QC
  getTlQc:         (id) => get(`/deals/${id}/tl-qc`),
  updateTlQc:      (id, data) => put(`/deals/${id}/tl-qc`, data),

  // Capital allocation
  getCapitalAllocation: (id) => get(`/deals/${id}/capital-allocation`),
  updateCapitalAllocation: (id, data) => put(`/deals/${id}/capital-allocation`, data),
  addCapitalLine:  (id, data) => post(`/deals/${id}/capital-allocation/lines`, data),
  capitalAllocationQueue: () => get('/capital-allocation/queue'),

  // Finance handover
  getFinanceHandover: (id) => get(`/deals/${id}/finance-handover`),
  updateFinanceHandover: (id, data) => put(`/deals/${id}/finance-handover`, data),
  assignFinance:   (id, data) => put(`/deals/${id}/finance/assign`, data),

  // Delivery
  getDelivery:     (id) => get(`/deals/${id}/delivery`),
  updateDelivery:  (id, data) => put(`/deals/${id}/delivery`, data),
  addDeliveryActivity: (id, data) => post(`/deals/${id}/delivery/activity`, data),

  // Repayment
  getRepayment:    (id) => get(`/deals/${id}/repayment`),
  updateRepayment: (id, data) => put(`/deals/${id}/repayment`, data),
  addRepaymentReceipt: (id, data) => post(`/deals/${id}/repayment/receipts`, data),
  verifyReceipt:   (id, receiptId, data) => put(`/deals/${id}/repayment/receipts/${receiptId}/verify`, data),
  xeroMatchReceipt:(id, receiptId, data) => put(`/deals/${id}/repayment/receipts/${receiptId}/xero-match`, data),

  // Reconciliation
  getReconciliation: (id) => get(`/deals/${id}/reconciliation`),
  updateReconciliation: (id, data) => put(`/deals/${id}/reconciliation`, data),
  closeDeal:       (id, data) => put(`/deals/${id}/close`, data),

  // Queues
  queueIc:         () => get('/queues/ic'),
  queueTlQc:       () => get('/queues/tl-qc'),
  queueFinance:    () => get('/queues/finance'),
  queueDelivery:   () => get('/queues/delivery'),
  queueRepayment:  () => get('/queues/repayment'),
  queueReconciliation: () => get('/queues/reconciliation'),

  // Tasks
  listTasks:       (params = {}) => get('/tasks?' + new URLSearchParams(params)),
  myTasks:         () => get('/tasks/my'),
  createTask:      (data) => post('/tasks', data),
  updateTask:      (id, data) => put(`/tasks/${id}`, data),

  // Documents & search
  searchDocuments: (params = {}) => get('/documents?' + new URLSearchParams(params)),
  search:          (q) => get('/search?' + new URLSearchParams({ q })),

  // Xero processing
  getXero:         (id) => get(`/deals/${id}/xero`),
  updateXero:      (id, data) => put(`/deals/${id}/xero`, data),
};
